import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrompt } from '@/contexts/PromptContext'
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea'

// Extend window interface for timeout and pending save
declare global {
  interface Window {
    exampleUpdateTimeout?: NodeJS.Timeout
    examplePendingSave?: () => Promise<void>
  }
}

interface ExampleStepProps {
  value?: string
  onChange?: (value: string) => void
  readOnly?: boolean
}

const ExampleStep: React.FC<ExampleStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation(['steps', 'common'])
  const [internalValue, setInternalValue] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const { promptData, updatePrompt, loading } = usePrompt()
  const lastSavedValueRef = useRef<string>('')

  // Use external value if provided (for refine mode), otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const setValue = externalOnChange || setInternalValue
  const placeholder = t('steps:example.placeholder')
  const textareaRef = useAutoResizeTextarea(value, placeholder)

  // Load existing example data when promptData is available
  // Only update if:
  // 1. Initial load (internalValue is empty), OR
  // 2. The local state matches what we last saved (user isn't actively typing)
  useEffect(() => {
    if (promptData?.example && !externalValue) {
      // Only update if this is initial load or if local state matches last saved value
      // This prevents real-time updates from overwriting user input while typing
      if (internalValue === '' || internalValue === lastSavedValueRef.current) {
        setInternalValue(promptData.example)
        lastSavedValueRef.current = promptData.example
      }
    }
  }, [promptData?.example, externalValue, internalValue])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.exampleUpdateTimeout) {
        clearTimeout(window.exampleUpdateTimeout)
      }
    }
  }, [])

  // Handle value changes with debounced database updates
  const handleValueChange = (newValue: string) => {
    setValue(newValue)
    
    // If we're in refine mode (external onChange), don't update database
    if (externalOnChange) return
    
    // Clear any existing timeout
    if (window.exampleUpdateTimeout) {
      clearTimeout(window.exampleUpdateTimeout)
    }
    
    // Create the save function
    const saveFunction = async () => {
      setIsUpdating(true)
      try {
        await updatePrompt({ example: newValue })
        // Update the ref to track what we just saved
        lastSavedValueRef.current = newValue
        // Clear the pending save after it completes
        delete window.examplePendingSave
      } catch (error) {
        console.error('Error updating example:', error)
        delete window.examplePendingSave
      } finally {
        setIsUpdating(false)
      }
    }
    
    // Store the save function so it can be executed immediately if needed
    window.examplePendingSave = saveFunction
    
    // Debounce database updates to avoid interfering with typing
    window.exampleUpdateTimeout = setTimeout(saveFunction, 1000) // 1 second debounce
  }

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('steps:example.title')}
      </h2>

      <p className="text-gray-600 mb-6 sm:mb-8 max-w-lg mx-auto px-4 text-xs sm:text-base">
        {t('steps:example.description')}
      </p>

      <div className="max-w-md mx-auto px-4">
        <textarea
          ref={textareaRef}
          placeholder={t('steps:example.placeholder')}
          value={value}
          onChange={(e) => handleValueChange(e.target.value)}
          readOnly={readOnly}
          disabled={loading}
          className="w-full min-h-12 p-3 text-sm sm:text-lg border border-gray-300 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
          rows={1}
        />
        {isUpdating && (
          <div className="mt-2 text-xs sm:text-sm text-gray-500">
            {t('common:status.saving')}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExampleStep
